export type FormatOptions = {
    shorten?: boolean // Turn on/off shortening of large numbers (e.g., 1k, 1M, 1B)
    decimals?: number // Amount of decimals to show in the formatted number (e.g., 1.23k)
    minDecimals?: number // Minimum fraction digits to always display (e.g., 2 forces 1 → 1.00)
    minSeparated?: number // Min amount of separating thousands with spaces (e.g., 1 234 567)
}

export function formatValue(
    value: number | undefined,
    options: FormatOptions = { shorten: false, decimals: 0 },
): string {
    if (value === undefined) {
        return ''
    }

    const { shorten, decimals, minDecimals, minSeparated = 10_000 } = options
    // for now only en locale without default grouping
    const fmt = new Intl.NumberFormat('en', {
        useGrouping: false,
        minimumFractionDigits: minDecimals ?? 0,
        maximumFractionDigits: decimals ?? 0,
    })

    if (shorten) {
        const abs = Math.abs(value)
        const suffixes = [
            { threshold: 1e9, suffix: 'B' },
            { threshold: 1e6, suffix: 'M' },
            { threshold: 1e3, suffix: 'k' },
        ]

        for (const { threshold, suffix } of suffixes) {
            if (abs >= threshold) {
                const n = value / threshold
                return fmt.format(n) + suffix
            }
        }
    }

    const baseStr = fmt.format(value)

    let sign = ''
    let numericStr = baseStr
    if (numericStr.startsWith('-')) {
        sign = '-'
        numericStr = numericStr.slice(1)
    }

    const [intPartRaw, fracPart] = numericStr.split('.')
    let intPart = intPartRaw

    if (Math.abs(Number(intPart)) >= minSeparated) {
        intPart = intPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    }

    const result = fracPart !== undefined ? `${intPart}.${fracPart}` : intPart

    return sign + result
}

const digitMap: Record<string, string> = {
    '0': '𝟎',
    '1': '𝟏',
    '2': '𝟐',
    '3': '𝟑',
    '4': '𝟒',
    '5': '𝟓',
    '6': '𝟔',
    '7': '𝟕',
    '8': '𝟖',
    '9': '𝟗',
}

export function mapDigitToUnicode(input: string): string {
    return input
        .split('')
        .map((char) => digitMap[char] ?? char) // replace if found, else keep original
        .join('')
}
